import { Box, Button, Export, Heading, Print, TabMenu, TabMenuItem, TextBlock } from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { CancelMark } from "components/icons/CancelMark";
import { ErasureIcon } from "components/icons/ErasureIcon";
import { LayeredLevels } from "components/icons/LayeredLevels";
import { RoundTick } from "components/icons/RoundTick";
import SelectedCheckmark from "components/icons/SelectedCheckmark";
import Share from "components/icons/Share";
import { CertifiedBadge } from "components/reports/passports/card/CertifiedBadge";
import { PassportShareDialog } from "components/reports/passports/card/PassportShareDialog";
import style from "components/reports/passports/passports-view.scss";
import {
    downloadArtifactPng,
    downloadArtifactSvg,
    PassportQrCodeTab,
    printArtifact,
} from "components/reports/passports/tabs/PassportArtifacts";
import {
    ARTIFACT_DISPLAY_HEIGHT_PIXELS,
    PassportArtifactCard,
} from "components/reports/passports/utils/artifactShared";
import { getDiagnosticStatus, isPass, Panel } from "components/reports/passports/utils/Panel";
import { getPassportHeading, getPassportIdentifiers } from "components/reports/passports/utils/passportDisplay";
import { PassportReport } from "services/report/passports/PassportService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const SECONDARY_BUTTON_ICON_COLOR = "var(--theme-primary)";

enum PassportTab {
    DETAILED,
    QR_CODE,
    STICKER,
    IMAGE,
}

const TABS = [
    {
        tab: PassportTab.DETAILED,
        labelKey: "DevicePassport.tabs.detailed",
        testId: testIds.workArea.passports.tabs.detailed,
    },
    {
        tab: PassportTab.QR_CODE,
        labelKey: "DevicePassport.tabs.qrCode",
        testId: testIds.workArea.passports.tabs.qrCode,
        artifactFileName: "device-passport-qr-code",
    },
    {
        tab: PassportTab.STICKER,
        labelKey: "DevicePassport.tabs.sticker",
        testId: testIds.workArea.passports.tabs.sticker,
        artifactFileName: "device-passport-sticker",
    },
    {
        tab: PassportTab.IMAGE,
        labelKey: "DevicePassport.tabs.image",
        testId: testIds.workArea.passports.tabs.image,
        artifactFileName: "device-passport-image",
    },
];

interface PassportResultCardProps {
    report: PassportReport;
    // The search value on screen, which a shared link looks the passport up with.
    searchValue: string;
}

export const PassportResultCard = (props: PassportResultCardProps) => {
    const { report } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const na = t("Common.na");
    const [shareDialogOpen, setShareDialogOpen] = React.useState(false);
    const [shareUrl, setShareUrl] = React.useState<string>();
    const [activeTab, setActiveTab] = React.useState(PassportTab.DETAILED);
    const artifactRef = React.useRef<HTMLDivElement>(null);
    const { device_information, clearance, erasure_details, diagnostic_result } = report;
    const passed = isPass(diagnostic_result);

    // The artefact is drawn over the content area, and the detail panels stay in it, hidden, on the other tabs. The
    // area is at least as tall as the tallest artefact plus the space above it, so the buttons below it are at the
    // same place on every tab.
    const contentAreaStyle: React.CSSProperties = {
        minHeight: `calc(${ARTIFACT_DISPLAY_HEIGHT_PIXELS}px + var(--spacing-sm))`,
    };

    // The QR code has no text, so it is downloaded as SVG, which scales to any label width. The others are PNG, because
    // their text would change font outside the page.
    const downloadArtifact = () => {
        const fileName = TABS.find(({ tab }) => tab === activeTab)?.artifactFileName ?? "device-passport";
        if (activeTab === PassportTab.QR_CODE) {
            downloadArtifactSvg(artifactRef.current, fileName);
            return;
        }
        downloadArtifactPng(artifactRef.current, fileName, theme.contentBackgroundColor);
    };

    const heading = getPassportHeading(device_information) ?? na;
    const identifiers = getPassportIdentifiers(t, device_information, report.last_processed);

    const clearanceRows = [
        { label: t("DevicePassport.panel.clearance.blacklisted"), value: clearance?.blacklisted },
        { label: t("DevicePassport.panel.clearance.graylisted"), value: clearance?.graylisted },
        { label: t("DevicePassport.panel.clearance.simlock"), value: clearance?.simlock },
        { label: t("DevicePassport.panel.clearance.lost_stolen_blocked"), value: clearance?.lost_stolen_blocked },
        { label: t("DevicePassport.panel.clearance.find_my_iphone"), value: clearance?.find_my_iphone },
        { label: t("DevicePassport.panel.clearance.frp_status"), value: clearance?.frp_status },
        { label: t("DevicePassport.panel.clearance.mdm_status"), value: clearance?.mdm_status },
        { label: t("DevicePassport.panel.clearance.abm_status"), value: clearance?.abm_status },
        { label: t("DevicePassport.panel.clearance.tcg_locked"), value: clearance?.tcg_locked },
        { label: t("DevicePassport.panel.clearance.autopilot_status"), value: clearance?.autopilot_status },
    ].filter((row): row is { label: string; value: string } => Boolean(row.value));

    const erasureRows = [
        { label: t("DevicePassport.panel.erasureDetails.standard"), value: erasure_details?.standard },
        {
            label: t("DevicePassport.panel.erasureDetails.result"),
            value: erasure_details?.result && (
                <Box verticalAlignment="Center" gap="Xxs">
                    {erasure_details.result.toLowerCase() === "successful" ? (
                        <SelectedCheckmark color={theme.accordianSuccessTextColor} />
                    ) : erasure_details.result.toLowerCase() === "failed" ? (
                        <CancelMark color={theme.errorTextColor} />
                    ) : null}
                    {erasure_details.result}
                </Box>
            ),
        },
        { label: t("DevicePassport.panel.erasureDetails.date"), value: erasure_details?.date },
        { label: t("DevicePassport.panel.erasureDetails.target.model"), value: erasure_details?.target?.model },
        { label: t("DevicePassport.panel.erasureDetails.target.type"), value: erasure_details?.target?.type },
        { label: t("DevicePassport.panel.erasureDetails.target.capacity"), value: erasure_details?.target?.capacity },
    ].filter((row) => row.value);

    return (
        <Box paddingBottom="Md" paddingTop="Xxs" columnLayout className={style.resultCard}>
            <Heading tag="div" variant="h2" disableTopSpacing disableBottomSpacing className={style.resultModel}>
                {heading}
            </Heading>
            <Box
                justifyContent="SpaceBetween"
                verticalAlignment="Center"
                gap="Sm"
                marginTop="Xxs"
                className={style.resultIdentifiers}
            >
                <Box gap="Sm" className={style.resultContent}>
                    {identifiers.map((id) => (
                        <TextBlock key={id} disableBottomSpacing>
                            {id}
                        </TextBlock>
                    ))}
                </Box>
                <CertifiedBadge className={style.resultContent} />
            </Box>
            <Box justifyContent="SpaceBetween" className={style.actionsRow}>
                <TabMenu className={style.tabMenu}>
                    {TABS.filter(({ tab }) => tab === PassportTab.DETAILED || shareUrl !== undefined).map(
                        ({ tab, labelKey, testId }) => (
                            <TabMenuItem
                                key={tab}
                                isActive={activeTab === tab}
                                onClick={() => setActiveTab(tab)}
                                testId={testId}
                            >
                                {t(labelKey)}
                            </TabMenuItem>
                        )
                    )}
                </TabMenu>
            </Box>
            <PassportShareDialog
                isOpen={shareDialogOpen}
                searchValue={props.searchValue}
                shareUrl={shareUrl}
                onLinkCreated={setShareUrl}
                onClose={() => setShareDialogOpen(false)}
            />
            <div className={style.contentArea} style={contentAreaStyle}>
                {shareUrl && activeTab !== PassportTab.DETAILED && (
                    <Box paddingTop="Sm" horizontalAlignment="Center" className={style.artifactBox}>
                        <div ref={artifactRef} className={style.printArtifact}>
                            {activeTab === PassportTab.QR_CODE && <PassportQrCodeTab shareUrl={shareUrl} />}
                            {(activeTab === PassportTab.STICKER || activeTab === PassportTab.IMAGE) && (
                                <PassportArtifactCard
                                    shareUrl={shareUrl}
                                    report={report}
                                    hasBrandedHeader={activeTab === PassportTab.IMAGE}
                                />
                            )}
                        </div>
                    </Box>
                )}
                <Box
                    gap="Md"
                    marginTop="Md"
                    className={classNames(style.detailPanelsRow, {
                        [style.detailPanelsRowHidden]: activeTab !== PassportTab.DETAILED,
                    })}
                >
                    <Panel
                        title={t("DevicePassport.panel.clearance.title")}
                        description={t("DevicePassport.panel.clearance.description")}
                        rows={clearanceRows.length > 0 ? clearanceRows : undefined}
                        icon={<LayeredLevels color={theme.linkTextColor} />}
                    />
                    <Panel
                        title={t("DevicePassport.panel.diagnostic.title")}
                        description={t("DevicePassport.panel.diagnostic.description")}
                        status={getDiagnosticStatus(passed, t)}
                        icon={<RoundTick color={theme.accordianSuccessTextColor} />}
                    />
                    <Panel
                        title={t("DevicePassport.panel.erasureDetails.title")}
                        description={t("DevicePassport.panel.erasureDetails.description")}
                        rows={erasureRows.length > 0 ? erasureRows : undefined}
                        icon={<ErasureIcon />}
                    />
                </Box>
            </div>
            <Box marginTop="Md" className={style.buttonsRow} gap="Sm">
                <Button
                    variant="Primary"
                    icon={<Print />}
                    onClick={() => {
                        if (activeTab !== PassportTab.DETAILED) {
                            printArtifact();
                            return;
                        }
                        usageStatisticsService.sendEvent({
                            category: Category.DEVICE_PASSPORT,
                            action: Action.PRINT_DEVICE_PASSPORT,
                        });
                        window.print();
                    }}
                    testId={testIds.workArea.passports.printButton}
                >
                    {t("DevicePassport.print")}
                </Button>
                {activeTab === PassportTab.DETAILED ? (
                    <Button
                        variant="Secondary"
                        icon={<Share color={SECONDARY_BUTTON_ICON_COLOR} />}
                        onClick={() => setShareDialogOpen(true)}
                        testId={testIds.workArea.passports.shareButton}
                        className={style.searchButton}
                    >
                        {t("Common.share")}
                    </Button>
                ) : (
                    <Button
                        variant="Secondary"
                        icon={<Export color={SECONDARY_BUTTON_ICON_COLOR} />}
                        onClick={downloadArtifact}
                        testId={testIds.workArea.passports.artifact.downloadButton}
                        className={style.searchButton}
                    >
                        {t(
                            activeTab === PassportTab.QR_CODE
                                ? "DevicePassport.artifact.downloadSvg"
                                : "DevicePassport.artifact.downloadPng"
                        )}
                    </Button>
                )}
            </Box>
        </Box>
    );
};
